import React, { useEffect, useState } from 'react';

import {
  BrowserRouter as Router,
  Switch,
  Route,
  Redirect,
  Link,
  NavLink
} from "react-router-dom";
import { HashLink } from 'react-router-hash-link';
import Cookies from 'js-cookie';
import Nav from 'react-bootstrap/Nav';
import Navbar from 'react-bootstrap/Navbar';
import NavDropdown from 'react-bootstrap/NavDropdown';
import { useDispatch, useSelector } from 'react-redux';

import { chooseDomain } from '../store/app';
import { fetchDomains, selectGoodDomains } from '../store/domains';
import { fetchAccount, fetchTimezones, fetchUser } from '../store/resources';
import AcceptableUse from './AcceptableUse';
import ContactForm from './ContactForm';
import DemoInstructions from './DemoInstructions';
import DomainList from './DomainList';
import BuyDomains from './BuyDomains';
import Home from './Home';
import PreviewCards from './PreviewCards';
import PreviewCardEdit from './PreviewCardEdit';
import LinkolnEmails from './LinkolnEmails';
import ForgotPassword from './ForgotPassword';
import UpdatePassword from './UpdatePassword';
import SetTimezone from './SetTimezone';
import Accounts from './Accounts';
import LogIn from './LogIn';
import LogOut from './LogOut';
import Privacy from './Privacy';
import SignUp from './SignUp';
import Terms from './Terms';
import VerifySignUp from './VerifySignUp';
import LinkReport from './LinkReport';
import '../App.css';

// [path, component, wrapper className]
const PUBLIC_ROUTES = [
  ['/privacy', Privacy, 'container mb-5 pb-5 px-0'],
  ['/terms', Terms, 'container mb-5 px-0'],
  ['/sign_up', SignUp, 'container py-5'],
  ['/contact_form', ContactForm, 'container pb-5 mt-5'],
  ['/forgot_password', ForgotPassword, 'container py-5'],
  ['/update_password/:code', UpdatePassword, 'container py-5 mt-5'],
  ['/complete_signup/:code', VerifySignUp, 'container py-5'],
  ['/demo_instructions', DemoInstructions, ''],
  ['/log_in', LogIn, 'container py-5 mt-5'],
  ['/acceptableuse', AcceptableUse, 'container py-5'],
];

const PRIVATE_ROUTES = [
  ['/views/:short_id', LinkReport, 'container py-5 mt-5'],
  ['/linkoln_emails', LinkolnEmails, 'container mb-5 py-5 mt-5'],
  ['/logout', LogOut, 'container py-5 mt-5'],
  ['/account/domains', DomainList, 'container py-5 mt-5'],
  ['/account/purchase', BuyDomains, 'container py-5 mt-5'],
  ['/update_password', UpdatePassword, 'container py-5 my-5'],
  ['/set_timezone', SetTimezone, 'container py-5 my-5'],
  ['/accounts', Accounts, 'container py-5 my-5'],
  ['/preview_card/:card_id', PreviewCardEdit, 'container py-5'],
  ['/preview_cards', PreviewCards, 'container mb-5 py-5 mt-5'],
];

const renderRoute = ([path, Component, className]) => (
  <Route key={path} path={path}>
    <div className={className}>
      <Component/>
    </div>
  </Route>
);

const MenuToggle = (props) => (
  <Navbar.Toggle {...props}>
    <span className="icon-bar top-bar"></span>
    <span className="icon-bar middle-bar"></span>
    <span className="icon-bar bottom-bar"></span>
  </Navbar.Toggle>
);

export default function App() {
  const dispatch = useDispatch();
  const { is_logged_in, account_id, current_domain } = useSelector(state => state.app);
  const good_domains = useSelector(selectGoodDomains);
  const [menu_open, setMenuOpen] = useState(false);
  const closeMenu = () => setMenuOpen(false);

  useEffect(() => {
    if (is_logged_in) {
      dispatch(fetchUser());
      dispatch(fetchAccount());
      dispatch(fetchDomains());
      dispatch(fetchTimezones());
    }
  }, [dispatch, is_logged_in, account_id]);

  const menuItem = (to, label) => (
    <NavDropdown.Item as={NavLink} to={to} onClick={closeMenu}>{label}</NavDropdown.Item>
  );

  return (
    <Router basename={process.env.PUBLIC_URL}>
    {is_logged_in ? (
    <header className="position-fixed w-100 shadow bg-white">
      <Navbar className="navbar-light" expand="lg" expanded={menu_open} onToggle={setMenuOpen}>
        <div className="container">
          <Link className="navbar-brand" to="/" onClick={closeMenu}><img src="images/LinkolnLogo.svg" className="logo" alt="Linkoln" /></Link>
          <MenuToggle aria-controls="userNav"/>
          <Navbar.Collapse id="userNav" className="justify-content-end p-md-0 pe-md-5">
            <Nav>
              <NavDropdown as="div" title={Cookies.get('name')}>
                {menuItem("/set_timezone", "Set Timezone")}
                {menuItem("/update_password", "Manage Password")}
                {menuItem("/logout", "Log Out")}
                {Cookies.get("is_admin") && menuItem("/accounts", "Accounts (Admin)")}
              </NavDropdown>
            </Nav>
            {current_domain && ( <>
            <Nav className="mr-0">
              <NavDropdown title={current_domain}>
                {good_domains.map(({ domain }) => (
                  <NavDropdown.Item key={domain} as="button" active={current_domain === domain} onClick={() => dispatch(chooseDomain(domain))}>{domain}</NavDropdown.Item>
                ))}
              </NavDropdown>
            </Nav>
            <Nav>
              <NavDropdown as="div" title="Services">
                {menuItem("/linkoln_emails", "Messages")}
                {menuItem("/preview_cards", "Link Previews")}
                {menuItem("/contact_form", "Contact us")}
              </NavDropdown>
            </Nav>
            </>)}
          </Navbar.Collapse>
        </div>
      </Navbar>
    </header>
    ) : (
    <header id="top" className="position-fixed w-100 shadow">
      <Navbar expand="lg" className="header" expanded={menu_open} onToggle={setMenuOpen}>
        <div className="container">
          <a className="navbar-brand" href="https://linkoln.io/"><img src="images/LinkolnLogo.svg" className="logo" alt="Linkoln" /></a>
          <MenuToggle aria-controls="nonUser"/>
          <Navbar.Collapse id="nonUser" className="justify-content-end">
            <ul className="navbar-nav">
              <li className="nav-item my-2 my-lg-0">
                <NavLink onClick={closeMenu} className="nav-link btn btn-danger text-white py-1 px-4 px-lg-3 mt-1 ms-lg-3 d-inline d-lg-block text-uppercase" to="/sign_up">Get Started</NavLink>
              </li>
              <li className="nav-item ml-lg-3 my-3 my-lg-0">
                <NavLink onClick={closeMenu} className="nav-link btn btn-secondary text-white py-1 px-5 px-lg-4 mt-1 ms-lg-3 d-inline d-lg-block text-uppercase" to="/log_in">Sign in</NavLink>
              </li>
            </ul>
          </Navbar.Collapse>
        </div>
      </Navbar>
    </header>
    )}

      <Switch>
        <Route exact path="/">
          <Home/>
        </Route>
        {PUBLIC_ROUTES.map(renderRoute)}
        {is_logged_in && PRIVATE_ROUTES.map(renderRoute)}
        <Route>
          <Redirect to="/"/>
        </Route>
      </Switch>
      <footer className="position-relative bg-secondary text-white py-5">
          <HashLink to="#back2Top" ></HashLink>
          <div className="container">
              <div className="row">
                  <div className="col-md-3">
                      <h4 className="mb-4">Site Links</h4>
                      <ul className="menu list-unstyled">
                        <small>
                          <li><Link to="/">Home</Link></li>
                          <li><Link to="/contact_form">Contact Us</Link></li>
                          <li><Link to="/sign_up" className="text-uppercase">Get Started</Link></li>
                          <li><Link to="/log_in">Sign In</Link></li>
                          <li><Link to="/privacy">Privacy</Link></li>
                          <li><Link to="/acceptableuse">Acceptable Use Policy</Link></li>
                        </small>
                      </ul>
                  </div>
              </div>
          </div>
      </footer>
    </Router>
  );
}
